<template>
  <BittsModal
    title="Edit Tags"
    :description="description"
    :width="480"
    :disabled="!hasChanges"
    :action-loading="isSaving"
    @action="emit('save', tagEdit)"
    @closed="emit('closed')"
  >
    <div class="partner-tab-edit-tags-modal">
      <PartnerTagsSelect v-model="addedLabels" :tags="addableTags" />
      <div>
        <h4 class="partner-tab-edit-tags-modal__heading">Common tags</h4>
        <ul
          v-if="keptCommonLabels.length"
          class="partner-tab-edit-tags-modal__tags"
          data-testid="common-tags"
        >
          <li v-for="label in keptCommonLabels" :key="label">
            <BittsPill
              :text="label"
              :aria-label="`Remove ${label}`"
              @click="removeCommonLabel(label)"
            />
          </li>
        </ul>
        <p v-else class="partner-tab-edit-tags-modal__empty">
          No common tags found.
        </p>
      </div>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsModal, BittsPill } from '@crossbeam/bitts';

import { computed, ref } from 'vue';

import {
  type PartnerWithTagsType,
  type TagEditType,
  commonTagLabels,
} from '@/components/partners/bulkTagEdit';
import PartnerTagsSelect from '@/components/partners/PartnerTagsSelect.vue';

import { usePartnersStore } from '@/stores';

const { partners, isSaving = false } = defineProps<{
  partners: PartnerWithTagsType[];
  isSaving?: boolean;
}>();

const emit = defineEmits<{
  save: [edit: TagEditType];
  closed: [];
}>();

const partnersStore = usePartnersStore();

const commonLabels = commonTagLabels(partners);
const removedLabels = ref<string[]>([]);
const addedLabels = ref<string[]>([]);

const description = computed(() =>
  partners.length === 1
    ? 'Manage tags for the selected partner.'
    : `Manage tags shared by all ${partners.length} selected partners.`,
);

const keptCommonLabels = computed(() =>
  commonLabels.filter((label) => !removedLabels.value.includes(label)),
);

const addableTags = computed(() =>
  partnersStore.partnerTags.filter(
    (tag) => !keptCommonLabels.value.includes(tag.label),
  ),
);

const tagEdit = computed<TagEditType>(() => ({
  removeLabels: removedLabels.value.filter(
    (label) => !addedLabels.value.includes(label),
  ),
  addLabels: addedLabels.value,
}));

const hasChanges = computed(
  () => !!tagEdit.value.removeLabels.length || !!tagEdit.value.addLabels.length,
);

function removeCommonLabel(label: string) {
  removedLabels.value.push(label);
}
</script>

<style scoped lang="pcss">
@reference "../../../../css/theme.css";
.partner-tab-edit-tags-modal {
  @apply flex flex-col gap-16;
}
.partner-tab-edit-tags-modal__heading {
  @apply text-sm font-bold text-neutral-text-strong mb-8;
}
.partner-tab-edit-tags-modal__tags {
  @apply flex flex-wrap gap-8;
}
.partner-tab-edit-tags-modal__empty {
  @apply text-base text-neutral-text;
}
</style>
